export enum AssetFamilyEnum {
  CREATIVE = 'creative',
  EDITORIAL = 'editorial',
}

export enum AssetTypeEnum {
  IMAGE = 'image',
  FILM = 'film',
  VIDEO_TEMPLATE = 'videotemplate',
}

export enum LicenseTypeEnum {
  RIGHTS_FREE = 'RF',
  RIGHTS_MANAGED = 'RM',
  RIGHTS_READY = 'RR',
  EDITORIAL = 'ED',
}

export enum MediaTypeEnum {
  PHOTOGRAPHY = 'photography',
  ILLUSTRATION = 'illustration',
  UNKNOWN = 'unknown',
}

export enum FileTypeEnum {
  EPS = 'eps',
}

export enum CollectionCodeEnum {
  BBA = 'BBA',
  ITNA = 'ITNA',
  NNBA = 'NNBA',
}

export enum FilmDefinitionEnum {
  FOUR_K = '4K',
  HD = 'HD',
  HD_WEB = 'HD_WEB',
  MEDIUM = 'MEDIUM',
}

// PascalCase to match the wire payload; distinct from searchParameters'
// lowercase OrientationEnum.
export enum AssetOrientationEnum {
  VERTICAL = 'Vertical',
  HORIZONTAL = 'Horizontal',
  PANORAMIC_VERTICAL = 'PanoramicVertical',
  PANORAMIC_HORIZONTAL = 'PanoramicHorizontal',
  SQUARE = 'Square',
}

// Agreement-prefix codes an asset can carry. Declaration order is priority
// order — agreementPrefix() returns the first match, so the highest-priority
// code stays at the top. Member keys mirror AgreementKindEnum (uppercase
// asset-prefix ↔ lowercase agreement kind); premium_pack is asset-only.
export enum AgreementPrefixEnum {
  ED = 'ED',
  PA = 'PA',
  RF = 'RF',
  IP = 'IP',
  PREMIUM_PACK = 'premium_pack',
  EA = 'EA',
}

export type AssetKeyword = Readonly<{
  text: string;
}>;

type AssetBase = Readonly<{
  id: string;
  mediaType: MediaTypeEnum;
  assetType: AssetTypeEnum;
  family: AssetFamilyEnum;
  licenseType: LicenseTypeEnum;
  orientation: AssetOrientationEnum;
  isOffline: boolean;
  releaseCode?: string;
  // Present on assets across collections; the offline arm narrows this to the
  // analog-archive CollectionCodeEnum.
  collectionCode?: string;
  landingUrl: string;
  // Present only for image and film assets. Distinct from `seoCaption` and
  // `altText`.
  caption?: string;
  seoCaption?: string;
  seoTitle: string;
  filmPreviewUrl?: string;
  artist: string;
  uploadDate: string;
  seoDuration: string;
  filmDefinition?: FilmDefinitionEnum;
  clipLength?: string;
  is360?: boolean;
  // Sent by one site only: the asset is licensed by request, so the fixed
  // tile's footer asks the visitor to call instead of offering a download.
  isCallForImage?: boolean;
  shortTitle: string;
  title?: string;
  people?: string;
  dateCreated?: string;
  // True only for editorial-family assets
  // flagged as exclusive content. Null when the asset service omits the flag;
  // the enriched `Asset` exposes it as a strict boolean.
  isExclusiveContent: boolean | null;
  collectionName?: string;
  // Present when the asset has a contributor record; absent for wholly-owned
  // assets, which credit `artist` instead (see `creditName`).
  contributorMemberName?: string;
  contributorDisplayName?: string;
  altText: string;
  // Agreement-prefix codes this asset carries. Drives purchasability and the
  // agreement prefix.
  agreements: AgreementPrefixEnum[];
  // Editorial-use-only restriction (allowed-use), independent of `family`: a
  // creative-family asset can still be editorial-use-only. The wire name is
  // `editorialOnly`; the enriched `Asset` exposes it as `isEditorialUseOnly`.
  editorialOnly: boolean;
  isIstockSignature: boolean;
  isAiEditable: boolean;
  fileType?: FileTypeEnum;
  usageInfo?: string;
  maxDimensions?: Readonly<{
    width: number;
    height: number;
  }>;
  keywords?: readonly AssetKeyword[];
  // Present only on assets grouped into a mosaic event tile (gallery.isGroupedByEvent()).
  eventId?: string;
  eventDate?: string;
  eventName?: string;

  collapsedImageCount?: number;
  collapsedVideoCount?: number;
  // Grid position within the results layout used for GA4 tracking context
  slot?: number;
}>;

// AssetDTO is the plain wire shape — the asset payload as the BFF serializes it.
// It is the factory input and the BFF response type; React components do not
// consume it directly, they use the enriched `Asset` below.
export type OnlineAssetDTO = AssetBase &
  Readonly<{
    isAnalogArchive: false;
    thumbUrl: string;
  }>;

export type OfflineAssetDTO = AssetBase &
  Readonly<{
    isAnalogArchive: true;
    collectionCode: CollectionCodeEnum;
  }>;

export type AssetDTO = OnlineAssetDTO | OfflineAssetDTO;

export interface AssetMethods {
  showLicenseBadge(): boolean;
  isOnlineEditorial(): boolean;
  isVerticalOrientation(): boolean;
  isVideoTemplate(): boolean;
  hasEditorialInfo(): boolean;
  isPurchasable(): boolean;
  agreementPrefix(): AgreementPrefixEnum | null;
  creditName(): string | undefined;
}

// The enriched form of a DTO arm: the wire fields with `editorialOnly` renamed
// to the domain `isEditorialUseOnly`, plus the predicate methods. Applied
// per-arm so each keeps its `isAnalogArchive` discriminant — `Omit` over the
// union would collapse to the common keys and drop `thumbUrl`/`collectionCode`.
type Enriched<TDto> = Omit<TDto, 'editorialOnly' | 'isExclusiveContent'> &
  Readonly<{ isEditorialUseOnly: boolean; isExclusiveContent: boolean }> &
  AssetMethods;

// Asset is the enriched, React-facing representation: the wire shape plus its
// predicate methods, with the allowed-use flag standardized to the domain name
// `isEditorialUseOnly` (the wire's `editorialOnly` is renamed in `fromDTO`).
// Built only via AssetFactory; the discriminant is still `isAnalogArchive`.
export type OnlineAsset = Enriched<OnlineAssetDTO>;
export type OfflineAsset = Enriched<OfflineAssetDTO>;

export type Asset = OnlineAsset | OfflineAsset;

// RF: no badge on editorial (RF editorial carries no RF mark). RM/ED: no
// badge on analog-archive assets (archive scans have no associated license).
const showLicenseBadge = (dto: AssetDTO): boolean => {
  if (dto.licenseType === LicenseTypeEnum.RIGHTS_FREE) {
    return dto.family !== AssetFamilyEnum.EDITORIAL;
  }
  if (
    dto.licenseType === LicenseTypeEnum.RIGHTS_MANAGED ||
    dto.licenseType === LicenseTypeEnum.EDITORIAL
  ) {
    return !dto.isAnalogArchive;
  }
  return false;
};

const isOnlineEditorial = (dto: AssetDTO): boolean =>
  dto.family === AssetFamilyEnum.EDITORIAL && !dto.isOffline;

const isVerticalOrientation = (dto: AssetDTO): boolean =>
  dto.orientation === AssetOrientationEnum.VERTICAL ||
  dto.orientation === AssetOrientationEnum.PANORAMIC_VERTICAL;

const isVideoTemplate = (dto: AssetDTO): boolean =>
  dto.assetType === AssetTypeEnum.VIDEO_TEMPLATE && !!dto.title;

const hasEditorialInfo = (dto: AssetDTO): boolean =>
  !!(dto.shortTitle || dto.people || dto.dateCreated);

// Purchasable outright: online, not an analog-archive scan, and not gated
// behind a Premium Access or Editorial agreement.
const isPurchasable = (dto: AssetDTO): boolean =>
  !dto.isOffline &&
  !dto.isAnalogArchive &&
  !dto.agreements.includes(AgreementPrefixEnum.PA) &&
  !dto.agreements.includes(AgreementPrefixEnum.ED);

// The single agreement prefix that represents an asset, by priority, or null
// when it carries none. Object.values() on a string enum preserves declaration
// (priority) order.
const agreementPrefix = (dto: AssetDTO): AgreementPrefixEnum | null =>
  Object.values(AgreementPrefixEnum).find((prefix) =>
    dto.agreements.includes(prefix)
  ) ?? null;

// The name an asset is credited to: its contributor's display name when it
// has a contributor record, otherwise the artist (wholly-owned assets).
const creditName = (dto: AssetDTO): string | undefined =>
  dto.contributorMemberName ? dto.contributorDisplayName : dto.artist;

const methods = (dto: AssetDTO): AssetMethods => ({
  showLicenseBadge: () => showLicenseBadge(dto),
  isOnlineEditorial: () => isOnlineEditorial(dto),
  isVerticalOrientation: () => isVerticalOrientation(dto),
  isVideoTemplate: () => isVideoTemplate(dto),
  hasEditorialInfo: () => hasEditorialInfo(dto),
  isPurchasable: () => isPurchasable(dto),
  agreementPrefix: () => agreementPrefix(dto),
  creditName: () => creditName(dto),
});

export class AssetFactory {
  // Merges the wire DTO with its method closures into a single object
  // literal. The result is the enriched `Asset` domain object the rest of the
  // app consumes — `isAnalogArchive` keeps the union narrowable.
  //
  // The search-results wire serializes the allowed-use restriction as
  // `editorialOnly`; the multi-site domain
  // standardizes on `isEditorialUseOnly`, so we rename it here — no consumer
  // sees the wire name. Both sites share this single wire key, so one rename
  // covers both. Once the BFF serializes `isEditorialUseOnly` directly, carry
  // the field through unchanged and drop this rename.
  fromDTO = (dto: AssetDTO): Asset => {
    if (dto.isAnalogArchive) {
      const { editorialOnly, ...rest } = dto;
      return {
        ...rest,
        isEditorialUseOnly: editorialOnly,
        isExclusiveContent: dto.isExclusiveContent === true,
        ...methods(dto),
      } satisfies OfflineAsset;
    }
    const { editorialOnly, ...rest } = dto;
    return {
      ...rest,
      isEditorialUseOnly: editorialOnly,
      isExclusiveContent: dto.isExclusiveContent === true,
      ...methods(dto),
    } satisfies OnlineAsset;
  };
}
